import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import CartIcon from "./CartIcon";
import MiniCart from "./MiniCart";
import LogoutConfirmModal from "./LogoutConfirmModal";
import CustomerNotificationBell from "./CustomerNotificationBell";
import { APP_LOGO_URL } from "../config/branding";
import { logout } from "../utils/authUtils";
import "./CustomerHeader.css";

const CustomerHeader = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showMiniCart, setShowMiniCart] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [dashboardPath, setDashboardPath] = useState("/customer/dashboard");

  // Role-based dashboard path mapping
  const getDashboardPath = (role) => {
    const roleMap = {
      R1: "/dashboard", // Owner
      R2: "/dashboard", // Admin
      R3: "/dashboard/professional", // Professional
      R4: "/dashboard/freelancer", // Freelancer
      R5: "/customer/dashboard", // Customer
      R6: "/portal-admin", // Portal Admin
      R7: "/dashboard/booth-renter", // Booth Renter
    };
    return roleMap[role] || "/customer/dashboard";
  };

  useEffect(() => {
    // Check authentication on mount
    const checkAuth = () => {
      const token = localStorage.getItem("ogestyle_token");
      const role = localStorage.getItem("ogestyle_role");
      setIsAuthenticated(!!token);
      if (token && role) {
        setDashboardPath(getDashboardPath(role));
      }
    };

    checkAuth();

    // Listen for auth changes (login/logout from other components)
    window.addEventListener("storage", checkAuth);
    window.addEventListener("authStateChanged", checkAuth);

    return () => {
      window.removeEventListener("storage", checkAuth);
      window.removeEventListener("authStateChanged", checkAuth);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleLogout = () => {
    logout(); // This now includes CRM widget reset
    setIsAuthenticated(false);
    setShowLogoutModal(false);
    closeMenu();
  };

  const promptLogout = () => {
    setShowLogoutModal(true);
  };

  return (
    <>
      <header className={`customer-header ${isScrolled ? "scrolled" : ""}`}>
        <div className="header-container">
          <Link to="/" className="logo" onClick={closeMenu}>
            <img
              src={APP_LOGO_URL}
              alt="OGESTYLE"
              className="logo-image"
              onError={(e) => {
                // Fallback to text logo if image fails to load
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
            <span className="logo-text-fallback" style={{ display: "none" }}>
              <span className="logo-icon">✨</span>
              <span className="logo-text">OGESTYLE</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="desktop-nav">
            <Link to="/" className="nav-link">
              Home
            </Link>
            <Link to="/shops" className="nav-link">
              Browse Shops
            </Link>
            <Link to="/freelancers" className="nav-link">
              Find Professionals
            </Link>
            <Link to="/products" className="nav-link">
              Shop Products
            </Link>
            {/* Packages sit beside the three things a customer can browse. They were reachable only
                from a home-page strip and the shop dashboard, which is why the catalogue had no
                entry point in the navigation at all. */}
            <Link to="/packages" className="nav-link">
              Packages
            </Link>
            {/* Public commercial pages. Pricing used to be invisible until after
                registration, which the owner asked to fix for transparency. */}
            <Link to="/features" className="nav-link">
              Features
            </Link>
            <Link to="/plans" className="nav-link">
              Pricing
            </Link>
            {isAuthenticated ? (
              <>
                <Link to={dashboardPath} className="nav-link">
                  Dashboard
                </Link>
                <button onClick={promptLogout} className="nav-link logout-btn">
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="nav-link login-btn">
                  Login
                </Link>
                <Link to="/signup" className="nav-link signup-btn">
                  Sign Up
                </Link>
              </>
            )}

            {/* Notifications (authenticated) */}
            <CustomerNotificationBell className="desktop-nav-bell" />

            {/* Cart Icon */}
            <CartIcon onClick={() => setShowMiniCart(!showMiniCart)} />
          </nav>

          {/* Mobile cart + hamburger */}
          <div className="mobile-header-actions">
            <CartIcon
              className="mobile-header-cart"
              onClick={() => setShowMiniCart(!showMiniCart)}
            />
            <CustomerNotificationBell className="mobile-header-bell" />
            <button
              className={`hamburger ${isMenuOpen ? "open" : ""}`}
              onClick={toggleMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`menu-overlay ${isMenuOpen ? "open" : ""}`}
        onClick={closeMenu}
      ></div>

      {/* Mobile Side Menu */}
      <nav className={`mobile-menu ${isMenuOpen ? "open" : ""}`}>
        <div className="mobile-menu-header">
          <span className="menu-logo">
            <img
              src={APP_LOGO_URL}
              alt=""
              style={{
                width: "24px",
                height: "24px",
                marginRight: "8px",
                verticalAlign: "middle",
              }}
              onError={(e) => (e.target.style.display = "none")}
            />
            OGESTYLE
          </span>
          <button className="close-btn" onClick={closeMenu}>
            &times;
          </button>
        </div>

        <div className="mobile-menu-content">
          <Link to="/" className="mobile-nav-link" onClick={closeMenu}>
            Home
          </Link>
          <Link to="/shops" className="mobile-nav-link" onClick={closeMenu}>
            Browse Shops
          </Link>
          <Link
            to="/freelancers"
            className="mobile-nav-link"
            onClick={closeMenu}
          >
            Find Professionals
          </Link>
          <Link
            to="/products"
            className="mobile-nav-link"
            onClick={closeMenu}
          >
            Shop Products
          </Link>
          <Link
            to="/packages"
            className="mobile-nav-link"
            onClick={closeMenu}
          >
            Packages
          </Link>
          <Link
            to="/features"
            className="mobile-nav-link"
            onClick={closeMenu}
          >
            Features
          </Link>
          <Link to="/plans" className="mobile-nav-link" onClick={closeMenu}>
            Pricing
          </Link>

          {isAuthenticated ? (
            <>
              <div className="menu-divider"></div>
              <Link
                to={dashboardPath}
                className="mobile-nav-link"
                onClick={closeMenu}
              >
                Dashboard
              </Link>
              <Link
                to="/notifications"
                className="mobile-nav-link"
                onClick={closeMenu}
              >
                Notifications
              </Link>
              <Link
                to="/looks/collections"
                className="mobile-nav-link"
                onClick={closeMenu}
              >
                Saved Looks
              </Link>
              {dashboardPath === "/customer/dashboard" && (
                <>
                  <Link
                    to="/customer/dashboard?tab=bookings"
                    className="mobile-nav-link"
                    onClick={closeMenu}
                  >
                    My Bookings
                  </Link>
                  <Link
                    to="/customer/dashboard?tab=orders"
                    className="mobile-nav-link"
                    onClick={closeMenu}
                  >
                    My Orders
                  </Link>
                  <Link
                    to="/customer/dashboard?tab=packages"
                    className="mobile-nav-link"
                    onClick={closeMenu}
                  >
                    My Packages
                  </Link>
                  <Link
                    to="/customer/dashboard?tab=profile"
                    className="mobile-nav-link"
                    onClick={closeMenu}
                  >
                    My Profile
                  </Link>
                </>
              )}

              <div className="menu-divider"></div>
              <button onClick={promptLogout} className="mobile-nav-link logout">
                Logout
              </button>
            </>
          ) : (
            <>
              <div className="menu-divider"></div>
              <Link
                to="/how-it-works"
                className="mobile-nav-link"
                onClick={closeMenu}
              >
                How It Works
              </Link>
              <Link to="/about" className="mobile-nav-link" onClick={closeMenu}>
                About
              </Link>

              <div className="menu-divider"></div>
              <Link
                to="/login"
                className="mobile-nav-link login"
                onClick={closeMenu}
              >
                Login
              </Link>
              <Link
                to="/signup"
                className="mobile-nav-link signup"
                onClick={closeMenu}
              >
                Sign Up
              </Link>
            </>
          )}
        </div>
      </nav>

      {/* Mini Cart Dropdown */}
      <MiniCart isOpen={showMiniCart} onClose={() => setShowMiniCart(false)} />

      <LogoutConfirmModal
        isOpen={showLogoutModal}
        onCancel={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
      />
    </>
  );
};

export default CustomerHeader;
